/**
 * Phase 27.47 — Address autocomplete input.
 *
 * Thin shadcn-styled wrapper on top of Google Places Autocomplete
 * (`AutocompleteService.getPlacePredictions` + `PlacesService.getDetails`).
 *
 * Behaviour:
 *   * As-you-type suggestions with a 250ms debounce.
 *   * USA-restricted by default (component + region hint).
 *   * On selection, emits BOTH the plain string via `onChange(value)`
 *     AND the parsed components via `onPlaceSelected({...})`.
 *   * When the API key is missing OR Places fails to load, silently
 *     degrades to a plain `<Input>` (form still submittable).
 *
 * Session tokens are used per open/close cycle to keep autocomplete
 * billing on the cheap "session" tier.
 *
 * Consumers pass their own value/onChange — the component is fully
 * controlled and does NOT store the address itself.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Loader2, MapPin } from 'lucide-react';
import { loadGoogleMaps } from '@/lib/googleMaps';

/** Extract the human-readable component out of a place details result. */
function pickComponent(components, type) {
  const c = components?.find((x) => x.types.includes(type));
  return c ? { short: c.short_name, long: c.long_name } : null;
}

/** Parse a `PlaceResult` into the fields our forms actually store.
 * Returns undefined-safe strings so callers can spread the result. */
function parsePlace(place) {
  const comps = place?.address_components || [];
  const streetNumber = pickComponent(comps, 'street_number')?.long;
  const route = pickComponent(comps, 'route')?.long;
  const subpremise = pickComponent(comps, 'subpremise')?.long;
  const street = [streetNumber, route].filter(Boolean).join(' ') || place?.name || '';
  return {
    formatted: place?.formatted_address || '',
    street: subpremise ? `${street} #${subpremise}` : street,
    city: (
      pickComponent(comps, 'locality')?.long
      || pickComponent(comps, 'sublocality')?.long
      || pickComponent(comps, 'postal_town')?.long
      || pickComponent(comps, 'administrative_area_level_2')?.long
      || ''
    ),
    state: pickComponent(comps, 'administrative_area_level_1')?.short || '',
    county: pickComponent(comps, 'administrative_area_level_2')?.long || '',
    zip: pickComponent(comps, 'postal_code')?.long || '',
    country: pickComponent(comps, 'country')?.short || '',
    lat: place?.geometry?.location?.lat?.() ?? null,
    lng: place?.geometry?.location?.lng?.() ?? null,
    place_id: place?.place_id || '',
  };
}

export default function AddressAutocomplete({
  value = '',
  onChange,
  onPlaceSelected,
  placeholder = 'Start typing an address…',
  restrictToUS = true,
  disabled = false,
  className = '',
  testId = 'address-autocomplete',
  types = ['address'],  // 'address' | 'geocode' | 'establishment'
  ...inputProps
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [failed, setFailed] = useState(false);

  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Load Google Maps once, then instantiate the services we need.
  useEffect(() => {
    let mounted = true;
    loadGoogleMaps()
      .then((maps) => {
        if (!mounted) return;
        if (!maps || !maps.places) {
          setFailed(true);
          return;
        }
        autocompleteServiceRef.current = new maps.places.AutocompleteService();
        // PlacesService needs a DOM node — a hidden div is fine.
        const dummy = document.createElement('div');
        placesServiceRef.current = new maps.places.PlacesService(dummy);
        sessionTokenRef.current = new maps.places.AutocompleteSessionToken();
        setReady(true);
      })
      .catch(() => { if (mounted) setFailed(true); });
    return () => { mounted = false; };
  }, []);

  // Close the dropdown when clicking outside the component.
  useEffect(() => {
    const onClickAway = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, []);

  const requestPredictions = useCallback((input) => {
    if (!ready || !autocompleteServiceRef.current || !input || input.length < 3) {
      setSuggestions([]); return;
    }
    setLoading(true);
    const request = {
      input,
      sessionToken: sessionTokenRef.current,
      types,
    };
    if (restrictToUS) {
      request.componentRestrictions = { country: ['us'] };
    }
    autocompleteServiceRef.current.getPlacePredictions(request, (preds, status) => {
      setLoading(false);
      // Google returns REQUEST_DENIED when the referrer or key is
      // rejected — surface that as a degraded state so the user gets
      // the "enter manually" hint instead of an empty dropdown.
      const OK = window.google?.maps?.places?.PlacesServiceStatus?.OK;
      const ZERO = window.google?.maps?.places?.PlacesServiceStatus?.ZERO_RESULTS;
      if (status && status !== OK && status !== ZERO) {
        setFailed(true);
        setSuggestions([]);
        return;
      }
      setSuggestions(preds || []);
      setHighlight(0);
    });
  }, [ready, restrictToUS, types]);

  const handleInputChange = (e) => {
    const v = e.target.value;
    onChange?.(v);
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => requestPredictions(v), 250);
  };

  const selectSuggestion = (prediction) => {
    if (!placesServiceRef.current) return;
    setOpen(false);
    setSuggestions([]);
    placesServiceRef.current.getDetails(
      {
        placeId: prediction.place_id,
        sessionToken: sessionTokenRef.current,
        fields: [
          'formatted_address', 'address_components', 'geometry',
          'name', 'place_id',
        ],
      },
      (place, status) => {
        if (status !== 'OK' && status !== window.google?.maps?.places?.PlacesServiceStatus?.OK) return;
        const parsed = parsePlace(place);
        onChange?.(parsed.formatted || parsed.street);
        onPlaceSelected?.(parsed);
        // Fresh session token for the next lookup — Google's billing
        // groups predictions + one details call under the same token.
        if (window.google?.maps?.places?.AutocompleteSessionToken) {
          sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        }
      },
    );
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      selectSuggestion(suggestions[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const showDropdown = open && (loading || suggestions.length > 0);

  // Graceful fallback: plain input when the key is missing / library
  // failed to load. Form is still fully usable.
  const isDegraded = useMemo(() => failed || (!ready && typeof window !== 'undefined' && !window.google), [failed, ready]);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <Input
        ref={inputRef}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => value && setOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        data-testid={testId}
        {...inputProps}
      />
      {loading && (
        <Loader2
          size={14}
          className="absolute right-2 top-1/2 -translate-y-1/2 animate-spin text-slate-400"
        />
      )}
      {showDropdown && (
        <ul
          className="absolute z-50 left-0 right-0 mt-1 max-h-72 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg"
          data-testid={`${testId}-list`}
        >
          {suggestions.map((s, i) => (
            <li
              key={s.place_id}
              onMouseDown={(e) => { e.preventDefault(); selectSuggestion(s); }}
              onMouseEnter={() => setHighlight(i)}
              className={`px-3 py-2 cursor-pointer text-sm flex items-start gap-2 ${
                i === highlight ? 'bg-slate-100' : 'hover:bg-slate-50'
              }`}
              data-testid={`${testId}-option-${i}`}
            >
              <MapPin size={13} className="mt-0.5 shrink-0 text-slate-400" />
              <div className="min-w-0">
                <div className="text-slate-900 truncate">
                  {s.structured_formatting?.main_text || s.description}
                </div>
                {s.structured_formatting?.secondary_text && (
                  <div className="text-xs text-slate-500 truncate">
                    {s.structured_formatting.secondary_text}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {isDegraded && (
        <p className="text-[10px] text-slate-400 mt-1 italic">
          Address suggestions unavailable — enter manually.
        </p>
      )}
    </div>
  );
}
